import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fileUrl, formatApiError } from "@/lib/api";

const ISLANDS = ["Viti Levu", "Vanua Levu", "Mamanuca Islands", "Yasawa Islands", "Kadavu", "Taveuni"];

export default function CreateListing() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        title: "",
        description: "",
        location_island: "Viti Levu",
        location_region: "",
        land_size_acres: 1,
        zoning: "Residential",
        price_fjd: 500000,
        ownership_type: "freehold",
        development_potential: "",
        listing_type: "sale",
        photos: [],
    });
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const update = (k, v) => setForm({ ...form, [k]: v });

    const onUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (form.photos.length + files.length > 30) {
            toast.error("Maximum 30 photos per listing");
            return;
        }
        const totalSize = files.reduce((s, f) => s + f.size, 0);
        if (totalSize > 30 * 1024 * 1024) {
            toast.error("Total photo size must not exceed 30 MB");
            return;
        }
        setUploading(true);
        const uploaded = [];
        for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            try {
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(data.id);
            } catch (err) {
                toast.error(`Failed to upload ${file.name}`);
            }
        }
        update("photos", [...form.photos, ...uploaded]);
        setUploading(false);
        toast.success(`${uploaded.length} photo(s) uploaded`);
    };

    const removePhoto = (id) => update("photos", form.photos.filter(p => p !== id));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/listings", {
                ...form,
                land_size_acres: Number(form.land_size_acres),
                price_fjd: Number(form.price_fjd),
            });
            toast.success("Listing published!");
            navigate("/dashboard");
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (detail === "phone_verification_required") {
                toast.error("Please verify your phone number first.");
                navigate("/verify-phone");
                return;
            }
            toast.error(formatApiError(detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="create-listing-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h1 className="font-display text-4xl font-bold mb-2">Create a new listing</h1>
            <p className="text-muted-foreground font-app mb-8">Up to 30 photos, 30 MB total. Fields marked * are required.</p>

            <Card className="p-8">
                <form onSubmit={submit} className="space-y-5">
                    <div>
                        <Label>Title *</Label>
                        <Input data-testid="listing-title" required value={form.title} onChange={(e) => update("title", e.target.value)} className="mt-1.5" placeholder="e.g. 5 acres beachfront — Coral Coast" />
                    </div>
                    <div>
                        <Label>Description *</Label>
                        <Textarea data-testid="listing-description" required value={form.description} onChange={(e) => update("description", e.target.value)} rows={4} className="mt-1.5" />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Island *</Label>
                            <Select value={form.location_island} onValueChange={(v) => update("location_island", v)}>
                                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>{ISLANDS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Region *</Label>
                            <Input data-testid="listing-region" required value={form.location_region} onChange={(e) => update("location_region", e.target.value)} className="mt-1.5" placeholder="Coral Coast, Savusavu..." />
                        </div>
                        <div>
                            <Label>Land size (acres) *</Label>
                            <Input data-testid="listing-acres" type="number" step="0.01" required value={form.land_size_acres} onChange={(e) => update("land_size_acres", e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Asking price (FJD) *</Label>
                            <Input data-testid="listing-price" type="number" required value={form.price_fjd} onChange={(e) => update("price_fjd", e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Zoning</Label>
                            <Select value={form.zoning} onValueChange={(v) => update("zoning", v)}>
                                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Residential">Residential</SelectItem>
                                    <SelectItem value="Tourism">Tourism</SelectItem>
                                    <SelectItem value="Commercial">Commercial</SelectItem>
                                    <SelectItem value="Agricultural">Agricultural</SelectItem>
                                    <SelectItem value="Mixed Use">Mixed Use</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Ownership type</Label>
                            <Select value={form.ownership_type} onValueChange={(v) => update("ownership_type", v)}>
                                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="freehold">Freehold</SelectItem>
                                    <SelectItem value="native_lease">Native Lease</SelectItem>
                                    <SelectItem value="crown_lease">Crown Lease</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Listing type</Label>
                            <Select value={form.listing_type} onValueChange={(v) => update("listing_type", v)}>
                                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="sale">For sale</SelectItem>
                                    <SelectItem value="jv">Joint Venture only</SelectItem>
                                    <SelectItem value="both">Sale or Joint Venture</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label>Development potential</Label>
                        <Textarea data-testid="listing-potential" value={form.development_potential} onChange={(e) => update("development_potential", e.target.value)} rows={3} className="mt-1.5" placeholder="What could be built here..." />
                    </div>

                    <div>
                        <Label>Photos (max 30, total 30 MB)</Label>
                        <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-3">
                            {form.photos.map((id) => (
                                <div key={id} className="relative aspect-square rounded-md overflow-hidden border border-border group">
                                    <img src={fileUrl(id)} alt="upload" className="w-full h-full object-cover" />
                                    <button type="button" onClick={() => removePhoto(id)} className="absolute top-1 right-1 bg-destructive text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition">
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                            {form.photos.length < 30 && (
                                <label data-testid="photo-upload-btn" className="aspect-square rounded-md border-2 border-dashed border-border hover:border-coral flex flex-col items-center justify-center cursor-pointer text-muted-foreground hover:text-coral transition">
                                    <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading} />
                                    {uploading ? <Upload className="h-5 w-5 animate-pulse" /> : <ImageIcon className="h-5 w-5" />}
                                    <span className="text-[10px] mt-1 font-app">Upload</span>
                                </label>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground font-app mt-2">{form.photos.length}/30 photos</p>
                    </div>

                    <Button type="submit" data-testid="create-listing-submit" disabled={submitting} className="w-full bg-ocean-green text-white h-12">
                        {submitting ? "Publishing…" : "Publish listing"}
                    </Button>
                </form>
            </Card>
        </div>
    );
}
